import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getSubadminPermissions } from '../api/subadmin';
import { getAgentPermissions } from '../api/agent';
import logo from '../Assets/jba-logo.png';
import { CiWallet } from "react-icons/ci";
import { getWalletBalance, getWalletTransactions } from '../api/wallet';
import WalletCard from './wallet/WalletCard';
import { IoMdChatboxes } from "react-icons/io";
import { IoMdNotifications, IoIosArrowBack, IoMdSend, IoMdMenu, IoIosArrowDropdown, IoMdClose, IoIosArrowDropup, IoIosArrowDropright } from 'react-icons/io';
import { CiLogout } from "react-icons/ci";
import { getChatRooms, getChatMessages, sendChatMessage } from '../api/chat';
import API from '../api/axios';
import { playSendSound, playReceiveSound } from '../utils/audio';
import { IoIosArrowForward } from "react-icons/io";
import io from 'socket.io-client';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../utils/canvasUtils';

const getRoleStyles = (role) => {
  const r = (role || '').toUpperCase();
  switch (r) {
    case 'AGENT':
      return { bg: '#fee2e2', text: '#991b1b' }; // Red
    case 'CA':
      return { bg: '#dbeafe', text: '#1e40af' }; // Blue
    case 'SUBADMIN':
      return { bg: '#dcfce7', text: '#166534' }; // Green
    case 'SUPERADMIN':
      return { bg: '#f3e8ff', text: '#6b21a8' }; // Purple
    default:
      return { bg: '#f3f4f6', text: '#374151' }; // Gray
  }
};

const getColorForSender = (senderId, role) => {
  if (role) return getRoleStyles(role).bg;

  const colors = [
    '#E1ECC8', '#E0F4FF', '#F0E4FF', '#FFE4E4', '#FFF4E0', '#E0FFE4', '#FFF0F5', '#F5F5DC'
  ];
  let hash = 0;
  const strId = String(senderId);
  for (let i = 0; i < strId.length; i++) {
    hash = strId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ProfileInfoCard = ({ icon, label, value, isAddress }) => (
  <div className="group flex items-start gap-4 p-3 bg-gray-50 rounded-xl hover:bg-purple-50 transition-colors border border-gray-100 hover:border-purple-100">
    <div className="w-9 h-9 shrink-0 rounded-lg bg-white flex items-center justify-center text-purple-600 shadow-sm mt-0.5">
      <i className={`fas fa-${icon}`}></i>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm font-semibold text-gray-700 ${isAddress ? 'line-clamp-3 leading-snug' : 'truncate'}`}>
        {value || <span className="text-gray-300 font-normal italic">Not provided</span>}
      </p>
    </div>
  </div>
);

const ProfileInput = ({ label, value, onChange, placeholder, type = "text", disabled = false, showToggle = false, onToggle = null, isVisible = false }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">{label}</label>
    <div className="relative">
      <input
        type={isVisible ? "text" : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full text-sm p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all font-medium ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-50'}`}
        placeholder={placeholder}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600 transition-colors"
        >
          <i className={`fas fa-eye${isVisible ? '-slash' : ''}`}></i>
        </button>
      )}
    </div>
  </div>
);

const Navbar = () => {
  const {
    user, setUser, logout, permissions,
    isCollapsed, setIsCollapsed,
    globalSearchQuery, setGlobalSearchQuery,
    showGlobalSearch, setShowGlobalSearch
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    setGlobalSearchQuery('');
    setShowGlobalSearch(false);
    setIsMobileNavOpen(false);
    navigate(path);
  };

  const activeNav = useMemo(() => location.pathname === '/' ? 'dashboard' : location.pathname === '/itr-application' ? 'itr' : location.pathname === '/customer' ? 'customer' : location.pathname === '/itr' ? 'itr-form' : location.pathname === '/customer-form' ? 'customer-form' : location.pathname === '/itr-history' ? 'itr-history' : 'dashboard', [location.pathname]);
  const activeNav2 = useMemo(() => location.pathname === '/subadmin' ? 'subdashboard' : location.pathname === '/itr-application' ? 'itr' : location.pathname === '/customer' ? 'customer' : location.pathname === '/itr' ? 'itr-form' : location.pathname === '/customer-form' ? 'customer-form' : location.pathname === '/itr-history' ? 'itr-history' : 'dashboard', [location.pathname]);

  const [subadminPermissions, setSubadminPermissions] = useState([]);
  const [agentPermissions, setAgentPermissions] = useState(permissions || []);
  const [walletBalance, setWalletBalance] = useState(user?.wbalance || 0);
  const [showWallet, setShowWallet] = useState(false);
  const [fetchtransactiondata, SetFetchTransactionData] = useState(false);
  const [walletTransaction, setWalletTransaction] = useState([]);
  const [showChatDropdown, setShowChatDropdown] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessageInput, setNewMessageInput] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const totalUnreadCount = useMemo(() => {
    return Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
  }, [unreadCounts]);

  const [notification, setNotification] = useState(null);
  const notificationTimerRef = useRef(null);
  const chatRoomsRef = useRef(chatRooms);
  const processedMessagesRef = useRef(new Set());

  useEffect(() => {
    chatRoomsRef.current = chatRooms;
  }, [chatRooms]);

  useEffect(() => {
    if (selectedRoom) {
      setUnreadCounts(prev => ({
        ...prev,
        [selectedRoom.id]: 0
      }));
      localStorage.setItem(`chat_room_viewed_${selectedRoom.id}`, new Date().toISOString());
      sessionStorage.setItem('active_chat_room_id', selectedRoom.id);
    } else {
      sessionStorage.removeItem('active_chat_room_id');
    }
    return () => {
      sessionStorage.removeItem('active_chat_room_id');
    };
  }, [selectedRoom?.id]);
  const chatContainerRef = useRef(null);
  const socketRef = useRef(null);
  const selectedRoomRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const chatDropdownRef = useRef(null);

  // Profile States
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.mail_id || user?.email || '',
    mobile_no: user?.mobile_no || '',
    profile_photo: user?.profile_photo || '',
    father_name: user?.father_name || '',
    address: user?.address || '',
    alternate_mobile_no: user?.alternate_mobile_no || ''
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Cropper States
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [tempImage, setTempImage] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If cropper is showing, don't close the dropdowns to avoid losing state
      if (showCropper) return;

      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (chatDropdownRef.current && !chatDropdownRef.current.contains(event.target)) {
        setShowChatDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCropper]);

  useEffect(() => {
    if (!showChatDropdown) {
      setSelectedRoom(null);
    }
  }, [showChatDropdown]);

  useEffect(() => {
    selectedRoomRef.current = selectedRoom;
  }, [selectedRoom]);


  // Password Change States
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [localSearchQuery, setLocalSearchQuery] = useState(globalSearchQuery || '');

  // Synchronize local search query when global search query changes (e.g., cleared from overlay)
  useEffect(() => {
    if (!globalSearchQuery) {
      setLocalSearchQuery('');
    }
  }, [globalSearchQuery]);

  const handleChangePassword = async () => {
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      alert("Please fill all password fields");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("New passwords do not match");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const response = await API.post('/auth/change-password', {
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword
      });

      if (response.status === 200) {
        alert("Password updated successfully!");
        setIsChangingPassword(false);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        alert(response.data?.message || "Failed to update password");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error updating password");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSearch = () => {
    if (localSearchQuery.trim()) {
      setGlobalSearchQuery(localSearchQuery);
      setShowGlobalSearch(true);
    }
  };

  const handleUpdateProfile = async () => {
    // Validation
    if (profileForm.name.length > 25) {
      alert("Name cannot exceed 25 characters");
      return;
    }
    if (profileForm.father_name.length > 25) {
      alert("Father's Name cannot exceed 25 characters");
      return;
    }
    if (profileForm.address.length > 60) {
      alert("Address cannot exceed 60 characters");
      return;
    }
    if (!/^\d{10}$/.test(profileForm.mobile_no)) {
      alert("Mobile number must be exactly 10 digits");
      return;
    }
    if (profileForm.alternate_mobile_no && !/^\d{10}$/.test(profileForm.alternate_mobile_no)) {
      alert("Alternate mobile number must be exactly 10 digits");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileForm.email)) {
      alert("Please enter a valid email address (e.g., example@domain.com)");
      return;
    }

    setIsUpdatingProfile(true);
    try {
      const response = await API.put('/agent/profile', {
        name: profileForm.name,
        mail_id: profileForm.email,
        mobile_no: profileForm.mobile_no,
        father_name: profileForm.father_name,
        address: profileForm.address,
        profile_photo: profileForm.profile_photo,
        alternate_mobile_no: profileForm.alternate_mobile_no
      });

      if (response.status === 200 || response.status === 201) {
        const newUser = {
          ...user,
          name: profileForm.name,
          mail_id: profileForm.email,
          mobile_no: profileForm.mobile_no,
          father_name: profileForm.father_name,
          address: profileForm.address,
          profile_photo: profileForm.profile_photo,
          alternate_mobile_no: profileForm.alternate_mobile_no
        };
        localStorage.setItem('user', JSON.stringify(newUser));
        setUser(newUser);
        alert("Profile updated successfully!");
        setIsEditingProfile(false);
      } else {
        alert(response.data?.message || response.data?.error || "Failed to update profile");
      }
    } catch (err) {
      console.error(err);
      alert("Error connecting to server");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(tempImage, croppedAreaPixels);
      const name = `profile_${user.id}_${Date.now()}.jpg`;
      const croppedFile = new File([croppedImageBlob], name, { type: 'image/jpeg' });

      setShowCropper(false);
      setIsUploadingPhoto(true);

      const formDataObj = new FormData();
      formDataObj.append('file', croppedFile);
      const response = await API.post('/upload/image', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      if (data.url) {
        setProfileForm(prev => ({ ...prev, profile_photo: data.url }));
        setToastMessage("Profile photo updated successfully!");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      }
      setTempImage(null);
    } catch (e) {
      console.error('Error cropping/uploading photo:', e);
      setShowCropper(false);
      alert("Failed to crop and upload photo");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleToastMouseEnter = () => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification(prev => prev ? { ...prev, isHovered: true } : null);
    
    // Set 3 minute timer (180,000 ms)
    notificationTimerRef.current = setTimeout(() => {
      setNotification(null);
    }, 180000);
  };

  const handleNotificationClick = () => {
    if (!notification) return;
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    const roomId = notification.room_id;
    setNotification(null);

    const isSubadmin = user && (user.issubadmin === true || user.issubadmin === 'subadmin' || user.issubadmin === 'true' || user.issubadmin === 1);
    const isSuperadmin = user && user.issuperadmin;

    if (isSuperadmin) {
      navigate('/superadmin/chat-rooms');
      sessionStorage.setItem('auto_select_chat_room_id', roomId);
    } else if (isSubadmin) {
      navigate('/subadmin/settings');
      sessionStorage.setItem('auto_select_chat_room_id', roomId);
    } else {
      // Open the dropdown and select the room
      setShowChatDropdown(true);
      const room = chatRoomsRef.current.find(r => r.id === roomId);
      if (room) {
        setSelectedRoom(room);
        setIsLoadingMessages(true);
        getChatMessages(room.id)
          .then(msgs => setMessages(msgs))
          .catch(err => console.error(err))
          .finally(() => setIsLoadingMessages(false));
      }
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, selectedRoom]);

  useEffect(() => {
    if (user?.isagent !== "verified") return;
    if (!fetchtransactiondata) return;
    let mounted = true;
    const timeout = setTimeout(() => {
      (async () => {
        try {
          const walletTransaction = await getWalletTransactions();
          if (!mounted) return;
          setWalletTransaction(walletTransaction);
        } catch (err) {
          console.error('Failed to fetch wallet transactions:', err);
        }
      })();
    }, 500);

    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, [user]);

  useEffect(() => {
    const isSubadmin = user && (user.issubadmin === true || user.issubadmin === 'subadmin' || user.issubadmin === 'true' || user.issubadmin === 1);
    const isAgent = user && (user.isagent === 'verified' || user.isagent === true || user.isagent === 'true');

    if (permissions && permissions.length > 0) {
      if (isSubadmin) setSubadminPermissions(permissions);
      if (isAgent) setAgentPermissions(permissions);
    }
  }, [permissions, user]);

  useEffect(() => {
    if (!user) return;
    const isAgent = user && (user.isagent === 'verified' || user.isagent === true || user.isagent === 'true');

    if (isAgent) {
      const fetchWalletBalance = async () => {
        try {
          const walletData = await getWalletBalance();
          setWalletBalance(walletData.balance);
        } catch (error) {
          console.error('Failed to fetch wallet balance:', error);
        }
      };
      fetchWalletBalance();
      const walletInterval = setInterval(fetchWalletBalance, 2 * 60 * 1000);
      window.walletInterval = walletInterval;
      return () => clearInterval(walletInterval);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      const baseURL = API.defaults.baseURL || '';
      const socketUrl = baseURL.endsWith('/api') ? baseURL.slice(0, -4) : window.location.origin;
      const socket = io(socketUrl, {
        path: '/api/socket.io',
        auth: { token: token ? token : null },
      });
      socketRef.current = socket;
      socket.on('connect', () => {
        console.log('Navbar Socket connected:', socket.id);
      });
      socket.on('room_message', (msg) => {
        if (processedMessagesRef.current.has(msg.id)) return;
        processedMessagesRef.current.add(msg.id);
        if (processedMessagesRef.current.size > 100) {
          const firstVal = processedMessagesRef.current.values().next().value;
          processedMessagesRef.current.delete(firstVal);
        }

        const localUser = JSON.parse(localStorage.getItem('user'));
        const isSubadmin = localUser && (localUser.issubadmin === true || localUser.issubadmin === 'subadmin' || localUser.issubadmin === 'true' || localUser.issubadmin === 1);
        const isCA = localUser && localUser.isca;
        const isSuperadmin = localUser && localUser.issuperadmin;
        const myRole = isSuperadmin ? 'Admin' : isCA ? 'CA' : isSubadmin ? 'Subadmin' : 'Agent';
        const isMe = msg.sender_id === localUser?.id && (
          (msg.sender_role || '').toLowerCase() === myRole.toLowerCase() ||
          ((msg.sender_role || '').toLowerCase() === 'superadmin' && myRole.toLowerCase() === 'admin')
        );

        console.log('[Socket Navbar] Message:', msg);
        console.log('[Socket Navbar] Local User:', localUser, 'myRole:', myRole);
        console.log('[Socket Navbar] isMe:', isMe);

        const activeRoomIdInStorage = sessionStorage.getItem('active_chat_room_id');
        const isCurrentlyViewingRoom = 
          (selectedRoomRef.current && String(selectedRoomRef.current.id) === String(msg.room_id)) || 
          (activeRoomIdInStorage && String(activeRoomIdInStorage) === String(msg.room_id));

        console.log('[Socket Navbar] activeRoomIdInStorage:', activeRoomIdInStorage);
        console.log('[Socket Navbar] isCurrentlyViewingRoom:', isCurrentlyViewingRoom);

        if (isCurrentlyViewingRoom) {
          setMessages(prev => {
            if (prev.find(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          localStorage.setItem(`chat_room_viewed_${msg.room_id}`, new Date().toISOString());
          if (!isMe) {
            playReceiveSound();
          }
        } else {
          if (!isMe) {
            playReceiveSound();
            setUnreadCounts(prev => ({
              ...prev,
              [msg.room_id]: (prev[msg.room_id] || 0) + 1
            }));

            // Find room name from ref
            const room = chatRoomsRef.current.find(r => r.id === msg.room_id);
            const rName = room ? room.name : `Room #${msg.room_id}`;
            if (!room) {
              fetchRooms();
            }

            // Show Toast Notification
            if (notificationTimerRef.current) {
              clearTimeout(notificationTimerRef.current);
            }

            setNotification({
              id: msg.id,
              message: msg.message,
              sender_name: msg.sender_name,
              sender_role: msg.sender_role,
              room_id: msg.room_id,
              room_name: rName,
              isHovered: false
            });

            // Auto dismiss after 8 seconds
            notificationTimerRef.current = setTimeout(() => {
              setNotification(null);
            }, 8000);
          }
        }

        // Update last message in the room list in real-time
        setChatRooms(prevRooms => {
          return prevRooms.map(r => {
            if (r.id === msg.room_id) {
              return {
                ...r,
                last_message: msg.message,
                last_message_time: msg.timestamp
              };
            }
            return r;
          });
        });
      });
      return () => {
        socket.disconnect();
        socketRef.current = null;
      };
    }
  }, [user?.id]);

  useEffect(() => {
    if (socketRef.current && selectedRoom) {
      socketRef.current.emit('join_room', { roomId: selectedRoom.id });
      return () => {
        if (socketRef.current && selectedRoom) {
          socketRef.current.emit('leave_room', { roomId: selectedRoom.id });
        }
      };
    }
  }, [selectedRoom?.id]);

  const fetchRooms = useCallback(async () => {
    if (!user) return;
    try {
      const rooms = await getChatRooms();
      setChatRooms(rooms);
      
      // Initialize unread counts
      const initialUnreads = {};
      rooms.forEach(room => {
        if (room.last_message) {
          const lastViewed = localStorage.getItem(`chat_room_viewed_${room.id}`);
          if (!lastViewed || new Date(room.last_message_time) > new Date(lastViewed)) {
            initialUnreads[room.id] = 1;
          }
        }
      });
      setUnreadCounts(initialUnreads);
    } catch (error) {
      console.error("Failed to fetch chat rooms", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchRooms();
    }
  }, [user?.id, fetchRooms]);

  if (!user) {
    return (
      <nav className="fixed top-0 left-0 right-0 bg-white shadow-md py-4 px-8 flex justify-between items-center z-50">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Logo" className="w-12 h-12" />
          <span className="ml-1 font-bold text-[20px] text-purple-700">JAIN BANDHU ASSOCIATE</span>
        </div>
      </nav>
    );
  }

  const isSubadmin = user && (user.issubadmin === true || user.issubadmin === 'subadmin' || user.issubadmin === 'true' || user.issubadmin === 1);
  const isAgentVerified = user && (user.isagent === 'verified' || user.isagent === true || user.isagent === 'true');
  const isCA = user && user.isca;
  const isSuperadmin = user && user.issuperadmin;
  const myRole = isSuperadmin ? 'Admin' : isCA ? 'CA' : isSubadmin ? 'Subadmin' : 'Agent';

  return (
    <div>
      {/* Top Navbar */}
      <nav className={`fixed top-0 right-0 z-50 transition-all duration-300 bg-white py-4 px-4 md:pr-8 flex justify-between items-center border-b shadow-sm ${isCollapsed ? 'md:left-20' : 'md:left-64'} left-0`}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="md:hidden text-gray-600 hover:text-purple-700 transition-colors p-1"
          >
            <IoIosArrowDropright size={28} />
          </button>
          <div className='text-xl md:text-2xl font-semibold whitespace-nowrap hidden lg:block'>
            {myRole === "Admin" ? "" : `${myRole} Dashboard`}
          </div>
        </div>

        {/* Global Search Bar */}
        <div className="flex-1 mx-4 hidden md:block max-w-xl">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="fas fa-search text-gray-400 group-focus-within:text-purple-500 transition-colors"></i>
            </div>
            <input
              type="text"
              placeholder={`Search Customers (PAN, Name, Mobile)...`}
              value={localSearchQuery}
              onChange={(e) => {
                setLocalSearchQuery(e.target.value);
                if (!e.target.value.trim()) {
                  setGlobalSearchQuery('');
                  setShowGlobalSearch(false);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              className="block w-full pl-10 pr-24 py-2.5 border border-gray-200 rounded-xl leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 sm:text-sm transition-all shadow-sm hover:bg-white"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 gap-1.5">
              {localSearchQuery && (
                <button
                  onClick={() => {
                    setLocalSearchQuery('');
                    setGlobalSearchQuery('');
                    setShowGlobalSearch(false);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear Search"
                >
                  <i className="fas fa-times-circle"></i>
                </button>
              )}
              <button
                onClick={handleSearch}
                className="bg-gradient-to-r from-purple-600 to-purple-800 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:shadow-lg hover:shadow-purple-500/20 active:scale-95 transition-all"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Right Menu Toggle */}
        <button
          onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
          className="md:hidden text-gray-600 hover:text-purple-700 transition-colors p-1"
        >
          {isMobileNavOpen ? <IoIosArrowDropup size={28} /> : <IoIosArrowDropdown size={28} />}
        </button>

        <div className="hidden md:flex items-center gap-4">
          <span className="text-gray-700 font-medium hidden md:block border-r pr-4 border-gray-200">Welcome, <span className="font-bold text-purple-700">{user?.name?.slice(0, 6) || user?.username || 'User'}..</span></span>

          {isAgentVerified && (
            <div onClick={() => handleNavigation('/wallet')} className={`text-white font-bold hover:bg-gray-800 transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg flex items-center gap-2 px-4 py-2 rounded-xl group ${walletBalance < 1000 ? 'bg-red-600 animate-pulse' : 'bg-gray-700'}`}>
              <CiWallet className='h-6 w-6 group-hover:scale-110 transition-transform' />
              <span>₹ {parseFloat(walletBalance || 0).toFixed(2)}</span>
            </div>
          )}

          {(isAgentVerified || isCA || isSubadmin || isSuperadmin) && (
            <div className='relative' ref={chatDropdownRef}>
              <div
                className='hover:bg-green-50 rounded-full cursor-pointer p-2 relative transition-colors group'
                onClick={async () => {
                  if (!showChatDropdown) {
                    setIsLoadingChat(true);
                    await fetchRooms();
                    setIsLoadingChat(false);
                  }
                  setShowChatDropdown(!showChatDropdown);
                  setShowProfileDropdown(false);
                }}
              >
                <IoMdChatboxes className='text-green-600 h-8 w-8 group-hover:text-green-500 transition-colors' />
                {totalUnreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-white shadow-sm animate-bounce">
                    {totalUnreadCount}
                  </span>
                )}
              </div>

              {showChatDropdown && (
                <div className='absolute right-0 mt-3 w-[400px] bg-white border border-gray-100 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200' style={{ maxHeight: '500px', height: '500px' }}>
                  {!selectedRoom ? (
                    <>
                      <div className="bg-[#7A306D] text-white px-5 py-4 text-sm font-bold flex justify-between items-center shrink-0 shadow-md">
                        <div className="flex items-center gap-2">
                          <IoMdChatboxes className="w-5 h-5" />
                          <span>Chat Rooms</span>
                          <span className="ml-2 text-xs bg-white/20 text-white px-2 py-0.5 rounded-full backdrop-blur-sm">{chatRooms.length}</span>
                        </div>
                        <button
                          onClick={() => setShowChatDropdown(false)}
                          className="hover:bg-white/20 p-1 rounded-full transition-colors"
                        >
                          <IoMdClose className="w-5 h-5" />
                        </button>
                      </div>
                      <div className='flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50'>
                        {isLoadingChat ? (
                          <div className='flex flex-col items-center justify-center h-full gap-2'>
                            <div className="w-8 h-8 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                            <p className="text-xs text-gray-400 font-medium">Fetching rooms...</p>
                          </div>
                        ) : chatRooms.length > 0 ? (
                          <ul className='divide-y divide-gray-100 p-2'>
                            {chatRooms.map((room) => (
                              <li
                                key={room.id}
                                className='m-1 px-4 py-3 bg-white hover:bg-purple-50 rounded-xl cursor-pointer transition-all flex items-center gap-3 border border-transparent hover:border-purple-100 shadow-sm'
                                onClick={async () => {
                                  setSelectedRoom(room);
                                  setUnreadCounts(prev => ({
                                    ...prev,
                                    [room.id]: 0
                                  }));
                                  localStorage.setItem(`chat_room_viewed_${room.id}`, new Date().toISOString());
                                  setIsLoadingMessages(true);
                                  try {
                                    const msgs = await getChatMessages(room.id);
                                    setMessages(msgs);
                                  } catch (error) {
                                    console.error("Failed to fetch messages", error);
                                  } finally {
                                    setIsLoadingMessages(false);
                                  }
                                }}
                              >
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center text-[#7A306D] font-black text-xs shrink-0 shadow-inner">
                                  {room.name.substring(0, 2).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className='font-bold text-gray-800 text-sm truncate'>{room.name}</p>
                                  <p className='text-[11px] text-gray-500 truncate flex items-center gap-1'>
                                    {room.last_message ? (
                                      <span className="truncate text-gray-600 font-medium">{room.last_message}</span>
                                    ) : (
                                      <>
                                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                                        <span>Active session</span>
                                      </>
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {unreadCounts[room.id] > 0 && (
                                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center shadow-sm">
                                      {unreadCounts[room.id]}
                                    </span>
                                  )}
                                  <i className="fas fa-chevron-right text-gray-300 text-[10px]"></i>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className='flex flex-col items-center justify-center h-full text-gray-400 p-8 text-center'>
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <IoMdChatboxes className="w-8 h-8 text-gray-300" />
                            </div>
                            <p className="text-sm font-semibold text-gray-500">No rooms available</p>
                            <p className="text-xs mt-1">You aren't associated with any chat rooms yet.</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-[#7A306D] text-white px-4 py-4 text-sm font-bold flex items-center gap-3 shrink-0 shadow-lg relative z-10">
                        <button
                          onClick={() => setSelectedRoom(null)}
                          className="hover:bg-white/20 p-1.5 rounded-lg transition-colors group"
                        >
                          <IoIosArrowBack className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                        </button>
                        <div className="flex-1 min-w-0">
                           <p className="truncate text-sm">{selectedRoom.name}</p>
                           <p className="text-[10px] text-purple-200 font-medium">Communication Channel</p>
                        </div>
                        <button
                          onClick={() => setShowChatDropdown(false)}
                          className="hover:bg-white/20 p-1.5 rounded-full transition-colors"
                        >
                          <IoMdClose className="w-5 h-5" />
                        </button>
                      </div>

                      <div
                        ref={chatContainerRef}
                        className='flex-1 overflow-y-auto p-4 bg-[#f8f9fc] flex flex-col space-y-4 custom-scrollbar'
                      >
                        {isLoadingMessages ? (
                          <div className='flex flex-col items-center justify-center h-full gap-2'>
                            <div className="w-6 h-6 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                          </div>
                        ) : messages.length > 0 ? (
                          messages.map((msg) => {
                            const isMe = msg.sender_id === user.id && (
                              (msg.sender_role || '').toLowerCase() === myRole.toLowerCase() ||
                              ((msg.sender_role || '').toLowerCase() === 'superadmin' && myRole.toLowerCase() === 'admin')
                            );
                            const roleStyles = getRoleStyles(msg.sender_role);
                            const bubbleColor = isMe ? '#7A306D' : '#ffffff';

                            return (
                              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-in slide-in-from-${isMe ? 'right' : 'left'}-4 duration-300`}>
                                <div
                                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm text-sm ${isMe ? 'rounded-br-none text-white' : 'rounded-bl-none text-gray-800 border border-gray-100'}`}
                                  style={{ backgroundColor: bubbleColor }}
                                >
                                  {!isMe && (
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-100" style={{ color: roleStyles.text }}>
                                        {msg.sender_role || 'Member'}
                                      </span>
                                    </div>
                                  )}
                                  <p className="leading-relaxed font-medium">{msg.message}</p>
                                  <div className={`flex items-center justify-end gap-1 mt-1.5 ${isMe ? 'text-purple-200' : 'text-gray-400'}`}>
                                    <span className="text-[9px] font-bold">
                                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {isMe && <i className="fas fa-check-double text-[8px]"></i>}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-gray-400 space-y-2">
                            <i className="far fa-comments text-3xl opacity-20"></i>
                            <p className="text-xs font-semibold">Start the conversation</p>
                          </div>
                        )}
                      </div>

                      <div className="p-3 bg-white border-t border-gray-100 shrink-0">
                        <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100 focus-within:border-purple-300 focus-within:ring-4 focus-within:ring-purple-500/5 transition-all">
                          <input
                            type="text"
                            value={newMessageInput}
                            onChange={(e) => setNewMessageInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const btn = document.getElementById('send-msg-btn');
                                if (btn) btn.click();
                              }
                            }}
                            placeholder="Type message here..."
                            className="flex-1 bg-transparent px-3 py-1.5 text-sm focus:outline-none font-medium placeholder:text-gray-400"
                          />
                          <button
                            id="send-msg-btn"
                            disabled={!newMessageInput.trim()}
                            onClick={async () => {
                              if (!newMessageInput.trim()) return;
                              const text = newMessageInput;
                              setNewMessageInput("");
                              try {
                                playSendSound();
                                await sendChatMessage(selectedRoom.id, text);
                              } catch (error) {
                                console.error("Failed to send message", error);
                                setNewMessageInput(text);
                              }
                            }}
                            className={`w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all shadow-md active:scale-90 ${newMessageInput.trim() ? 'bg-gradient-to-br from-purple-600 to-purple-800 hover:shadow-purple-500/30' : 'bg-gray-300 cursor-not-allowed shadow-none'
                              }`}
                          >
                            <IoMdSend className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {isAgentVerified && (
            <div className='relative' ref={profileDropdownRef}>
              <div
                className='hover:bg-purple-100 rounded-full cursor-pointer p-1 pr-3 flex items-center gap-2 group transition-all duration-200 border border-transparent hover:border-purple-200'
                onClick={() => {
                  setShowProfileDropdown(!showProfileDropdown);
                  setShowChatDropdown(false);
                  setIsEditingProfile(false);
                }}
              >
                {user.profile_photo ? (
                  <img src={user.profile_photo} alt="Profile" className="w-9 h-9 rounded-full border-2 border-purple-200 group-hover:border-purple-400 object-cover transition-colors" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold group-hover:bg-purple-700 transition-colors shadow-sm">
                    {(user.name || user.username || 'A').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-bold text-gray-800 leading-tight truncate max-w-[120px]">{user.name || user.username}</p>
                  <p className="text-[10px] text-purple-600 font-semibold uppercase tracking-wider">Agent</p>
                </div>
              </div>

              {showProfileDropdown && (
                <div className='absolute right-0 mt-3 w-85 bg-white border border-gray-100 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200' style={{ width: '340px' }}>
                  <div className="bg-[#7A306D] text-white px-6 py-6 text-center relative overflow-hidden shrink-0">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-400/10 rounded-full -ml-12 -mb-12 blur-xl"></div>

                    <div className="relative inline-block mb-3 group">
                      {profileForm.profile_photo ? (
                        <img src={profileForm.profile_photo} alt="Profile" className="w-20 h-20 rounded-full border-4 border-white/20 mx-auto object-cover shadow-lg" />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold mx-auto border-4 border-white/20 shadow-lg">
                          {(user.name || user.username || 'A').charAt(0).toUpperCase()}
                        </div>
                      )}
                      {isEditingProfile && (
                        <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                          {isUploadingPhoto ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <i className="fas fa-camera text-white"></i>
                          )}
                          <input type="file" className="hidden" accept="image/*" onChange={handlePhotoChange} disabled={isUploadingPhoto} />
                        </label>
                      )}
                      <div className="absolute bottom-1 right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-purple-800"></div>
                    </div>
                    <h3 className="font-bold text-lg truncate px-2">{user.name || user.username}</h3>
                    <p className="text-purple-200 text-xs truncate px-2 opacity-90">{user.mail_id || user.email}</p>
                  </div>

                  <div className="flex-1 overflow-y-auto custom-scrollbar" style={{ maxHeight: '400px' }}>
                    <div className="p-5 space-y-4">
                      {!isEditingProfile && !isChangingPassword ? (
                        <>
                          <div className="grid grid-cols-1 gap-3">
                            <ProfileInfoCard icon="user-tag" label="Name" value={user.name} />
                            <ProfileInfoCard icon="user-friends" label="Father's Name" value={user.father_name} />
                            <ProfileInfoCard icon="phone-alt" label="Mobile Number" value={user.mobile_no} />
                            <ProfileInfoCard icon="phone-square-alt" label="Alt. Mobile" value={user.alternate_mobile_no} />
                            <ProfileInfoCard icon="envelope" label="Email ID" value={user.mail_id || user.email} />
                            <ProfileInfoCard icon="map-marker-alt" label="Address" value={user.address} isAddress />
                          </div>

                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                setProfileForm({
                                  name: user.name || '',
                                  email: user.mail_id || user.email || '',
                                  mobile_no: user.mobile_no || '',
                                  profile_photo: user.profile_photo || '',
                                  father_name: user.father_name || '',
                                  address: user.address || '',
                                  alternate_mobile_no: user.alternate_mobile_no || ''
                                });
                                setIsEditingProfile(true);
                              }}
                              className="w-full flex items-center justify-center gap-3 py-3 text-sm font-bold text-purple-700 bg-purple-50 rounded-xl hover:bg-purple-100 transition-all active:scale-95 border border-purple-100"
                            >
                              <i className="fas fa-user-edit"></i> Edit Profile
                            </button>

                            <button
                              onClick={() => setIsChangingPassword(true)}
                              className="w-full flex items-center justify-center gap-3 py-3 text-sm font-bold text-orange-700 bg-orange-50 rounded-xl hover:bg-orange-100 transition-all active:scale-95 border border-orange-100"
                            >
                              <i className="fas fa-key"></i> Change Password
                            </button>
                          </div>
                        </>
                      ) : isChangingPassword ? (
                        <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300">
                          <h4 className="text-sm font-bold text-gray-700 border-b pb-2 flex items-center gap-2">
                            <i className="fas fa-lock text-orange-500"></i> Update Security
                          </h4>

                          <ProfileInput
                            label="Old Password"
                            type="password"
                            value={passwordForm.oldPassword}
                            onChange={(val) => setPasswordForm({ ...passwordForm, oldPassword: val })}
                            placeholder="Enter current password"
                            showToggle={true}
                            isVisible={showPasswords.old}
                            onToggle={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                          />

                          <ProfileInput
                            label="New Password"
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={(val) => setPasswordForm({ ...passwordForm, newPassword: val })}
                            placeholder="Enter new password"
                            showToggle={true}
                            isVisible={showPasswords.new}
                            onToggle={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          />

                          <ProfileInput
                            label="Confirm New Password"
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={(val) => setPasswordForm({ ...passwordForm, confirmPassword: val })}
                            placeholder="Re-enter new password"
                            showToggle={true}
                            isVisible={showPasswords.confirm}
                            onToggle={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          />

                          <div className="flex gap-3 pt-2">
                            <button
                              onClick={() => {
                                setIsChangingPassword(false);
                                setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                              }}
                              className="flex-1 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 bg-gray-50 rounded-xl transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleChangePassword}
                              disabled={isUpdatingPassword}
                              className="flex-1 py-3 text-sm font-bold bg-gradient-to-r from-orange-500 to-orange-700 text-white rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100"
                            >
                              {isUpdatingPassword ? (
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                  <span>Saving</span>
                                </div>
                              ) : 'Update Password'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300">
                          <ProfileInput label="Full Name" value={profileForm.name} onChange={(val) => setProfileForm({ ...profileForm, name: val.slice(0, 25) })} placeholder="Name" disabled={true} />
                          <ProfileInput label="Father's Name" value={profileForm.father_name} onChange={(val) => setProfileForm({ ...profileForm, father_name: val.slice(0, 25) })} placeholder="Enter father's name" />
                          <ProfileInput label="Mobile Number" value={profileForm.mobile_no} onChange={(val) => setProfileForm({ ...profileForm, mobile_no: val.replace(/\D/g, '').slice(0, 10) })} placeholder="Mobile Number" disabled={true} />
                          <ProfileInput label="Alt. Mobile" value={profileForm.alternate_mobile_no} onChange={(val) => setProfileForm({ ...profileForm, alternate_mobile_no: val.replace(/\D/g, '').slice(0, 10) })} placeholder="Enter alternate mobile" />
                          <ProfileInput label="Email Address" value={profileForm.email} onChange={(val) => setProfileForm({ ...profileForm, email: val })} placeholder="Enter email address" type="email" />
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">Address ({profileForm.address.length}/60)</label>
                            <textarea
                              value={profileForm.address}
                              onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value.slice(0, 60) })}
                              className="w-full text-sm p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all font-medium min-h-[80px] resize-none"
                              placeholder="Enter your full address"
                            />
                          </div>

                          <div className="flex gap-3 pt-2">
                            <button
                              onClick={() => setIsEditingProfile(false)}
                              className="flex-1 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 bg-gray-50 rounded-xl transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdateProfile}
                              disabled={isUpdatingProfile || isUploadingPhoto}
                              className="flex-1 py-3 text-sm font-bold bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100"
                            >
                              {isUpdatingProfile ? (
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                  <span>Saving</span>
                                </div>
                              ) : 'Save Changes'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 border-t border-gray-100 shrink-0">
                    <button
                      onClick={() => {
                        localStorage.clear();
                        logout();
                        handleNavigation('/login');
                      }}
                      className="w-full flex items-center justify-center gap-3 py-3 text-sm font-bold text-red-600 bg-white rounded-xl hover:bg-red-50 hover:text-red-700 transition-all active:scale-95 border border-red-100 group shadow-sm"
                    >
                      <CiLogout className="h-5 w-5 transition-transform group-hover:-translate-x-1" /> Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {!isAgentVerified && (
            <button
              onClick={() => {
                localStorage.clear();
                logout();
                handleNavigation('/login');
              }}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md font-medium transition duration-300 ease-in-out"
            >
              Logout
            </button>
          )}
        </div>

        {/* Mobile Navigation Dropdown */}
        <div className={`absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 p-4 flex flex-col gap-4 transform transition-all duration-300 origin-top md:hidden ${isMobileNavOpen ? 'scale-y-100 opacity-100 visible' : 'scale-y-0 opacity-0 invisible'}`}>
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-lg border border-purple-200">
                {(user.name || user.username || 'A').charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-gray-800">{user.name || user.username}</p>
                <p className="text-xs text-gray-500 capitalize font-medium">{myRole}</p>
              </div>
            </div>
          </div>

          {isAgentVerified && (
            <div onClick={() => handleNavigation('/wallet')} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl active:bg-gray-100 transition-colors cursor-pointer border border-gray-100">
              <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                <CiWallet className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Wallet Balance</p>
                <p className="text-sm font-bold text-gray-800">₹ {parseFloat(walletBalance || 0).toFixed(2)}</p>
              </div>
              <IoIosArrowForward className="text-gray-400" />
            </div>
          )}

          {(isAgentVerified || isCA || isSubadmin || isSuperadmin) && (
            <div onClick={() => { setShowChatDropdown(!showChatDropdown); setIsMobileNavOpen(false); }} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl active:bg-gray-100 transition-colors cursor-pointer border border-gray-100">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center relative">
                <IoMdChatboxes className="w-5 h-5" />
                {totalUnreadCount > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></span>}
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-800">Messages</p>
              </div>
              <IoIosArrowForward className="text-gray-400" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-2">
            {isAgentVerified && (
              <button onClick={() => { setShowProfileDropdown(true); setIsMobileNavOpen(false); }} className="p-3 bg-purple-50 text-purple-700 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border border-purple-100 active:scale-95 transition-transform">
                <i className="fas fa-user"></i> Profile
              </button>
            )}
            <button onClick={() => { logout(); handleNavigation('/login'); }} className={`p-3 bg-red-50 text-red-600 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border border-red-100 active:scale-95 transition-transform ${!isAgentVerified ? 'col-span-2' : ''}`}>
              <CiLogout className="w-5 h-5" /> Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Vertical Sidebar */}
      {
        isSuperadmin ? (
          // Superadmin left-nav: show superadmin specific links
          <aside className={`fixed top-0 left-0 h-screen ${isCollapsed ? 'w-0 md:w-20' : 'w-64'} transition-all duration-300 bg-[#7A306D] text-white flex flex-col pt-4 space-y-1 z-50 border-r border-purple-800 shadow-xl overflow-y-auto overflow-x-hidden`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-6'} py-4 mb-2`}>
              <img src={logo} alt="Logo" className="w-10 h-10 bg-white rounded-lg p-1 shrink-0" />
              {!isCollapsed && <span className="font-bold text-sm tracking-tight text-white truncate">JBA</span>}
            </div>

            <button
              onClick={() => handleNavigation('/superadmin')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/superadmin' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Dashboard' : ''}
            >
              <i className="fas fa-tachometer-alt w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Dashboard</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/agents')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/agents') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Agents' : ''}
            >
              <i className="fas fa-user-tie w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Agents</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/ratecard')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/ratecard') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Rate Card/Penalty' : ''}
            >
              <i className="fas fa-file-invoice-dollar w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Rate Card/Penalty</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/wallets')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/wallets') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Wallets' : ''}
            >
              <i className="fas fa-wallet w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Wallets</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/subadmins')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/subadmins') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Subadmins' : ''}
            >
              <i className="fas fa-users-cog w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Subadmins</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/cas')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/cas') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'CAs' : ''}
            >
              <i className="fas fa-user-graduate w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">CAs</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/customers')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/customers') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Customers' : ''}
            >
              <i className="fas fa-users w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Customers</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/payments')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/payments') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Payments' : ''}
            >
              <i className="fas fa-credit-card w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Payments</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/customer-form-fields')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/customer-form-fields') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Form Fields' : ''}
            >
              <i className="fas fa-list-alt w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Form Fields</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/ITR')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/ITR') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'ITR' : ''}
            >
              <i className="fas fa-file-alt w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">ITR</span>}
            </button>

            <button
              onClick={() => handleNavigation('/superadmin/chat-rooms')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/chat-rooms') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Chat Rooms' : ''}
            >
              <i className="fas fa-comments w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Chat Rooms</span>}
            </button>


            <button
              onClick={() => handleNavigation('/superadmin/reports')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/superadmin/reports') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Reports' : ''}
            >
              <i className="fas fa-chart-bar w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Reports</span>}
            </button>

            <div className="mt-auto pb-4 px-3">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="w-full flex items-center justify-center py-3 bg-purple-900/30 hover:bg-purple-900/50 rounded-xl transition-all duration-300 group"
              >
                {isCollapsed ? (
                  <IoIosArrowForward className="w-5 h-5 text-purple-200 group-hover:scale-125 transition-transform" />
                ) : (
                  <div className="flex items-center gap-3">
                    <IoIosArrowBack className="w-5 h-5 text-purple-200 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold uppercase tracking-widest text-purple-100">Collapse</span>
                  </div>
                )}
              </button>
            </div>
          </aside>
        ) : isCA ? (
          // CA left-nav: only show ITRs Assigned
          <aside className={`fixed top-0 left-0 h-screen ${isCollapsed ? 'w-0 md:w-20' : 'w-64'} transition-all duration-300 bg-[#7A306D] text-white flex flex-col pt-4 space-y-2 z-50 border-r border-purple-800 shadow-xl overflow-x-hidden`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-6'} py-4 mb-4`}>
              <img src={logo} alt="Logo" className="w-10 h-10 bg-white rounded-lg p-1 shrink-0" />
              {!isCollapsed && <span className="font-bold text-sm tracking-tight text-white truncate">CA PANEL</span>}
            </div>
            <button
              onClick={() => handleNavigation('/ca')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/ca' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'ITRs Assigned' : ''}
            >
              <i className="fas fa-file-alt w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">ITRs Assigned</span>}
            </button>
            <button
              onClick={() => handleNavigation('/ca/reports')}
              className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/ca/reports' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
              title={isCollapsed ? 'Reports' : ''}
            >
              <i className="fas fa-chart-bar w-5 shrink-0"></i>
              {!isCollapsed && <span className="text-sm font-medium truncate">Reports</span>}
            </button>

            <div className="mt-auto pb-4 px-3">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="w-full flex items-center justify-center py-3 bg-purple-900/30 hover:bg-purple-900/50 rounded-xl transition-all duration-300 group"
              >
                {isCollapsed ? (
                  <IoIosArrowForward className="w-5 h-5 text-purple-200 group-hover:scale-125 transition-transform" />
                ) : (
                  <div className="flex items-center gap-3">
                    <IoIosArrowBack className="w-5 h-5 text-purple-200 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold uppercase tracking-widest text-purple-100">Collapse</span>
                  </div>
                )}
              </button>
            </div>
          </aside>
        ) : isSubadmin ? (
          // Subadmin left-nav: show subadmin specific links based on permissions
          <aside className={`fixed top-0 left-0 h-screen ${isCollapsed ? 'w-0 md:w-20' : 'w-64'} transition-all duration-300 bg-[#7A306D] text-white flex flex-col pt-4 space-y-1 z-50 border-r border-purple-800 shadow-xl overflow-y-auto overflow-x-hidden`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-6'} py-4 mb-4`}>
              <img src={logo} alt="Logo" className="w-10 h-10 bg-white rounded-lg p-1 shrink-0" />
              {!isCollapsed && <span className="font-bold text-sm tracking-tight text-white truncate">SUBADMIN PANEL</span>}
            </div>

            {subadminPermissions.includes('manage_itr') && (
              <button
                onClick={() => handleNavigation('/subadmin/customers')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/customers') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'Manage ITRs' : ''}
              >
                <i className="fas fa-cogs w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">Manage ITRs</span>}
              </button>
            )}

            {subadminPermissions.includes('manage_customers') && (
              <button
                onClick={() => handleNavigation('/subadmin/customers')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/customers') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'Customers' : ''}
              >
                <i className="fas fa-users w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">Customers</span>}
              </button>
            )}

            {subadminPermissions.includes('manage_agents') && (
              <button
                onClick={() => handleNavigation('/subadmin/agents')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/agents') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'Agents' : ''}
              >
                <i className="fas fa-user-tie w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">Agents</span>}
              </button>
            )}

            {subadminPermissions.includes('manage_chat_room') && (
              <button
                onClick={() => handleNavigation('/subadmin/settings')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/settings') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'Manage Chat Room' : ''}
              >
                <i className="fas fa-sliders-h w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">Manage Chat Room</span>}
              </button>
            )}

            {subadminPermissions.includes('view_reports') && (
              <button
                onClick={() => handleNavigation('/subadmin/reports')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/reports') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'Reports' : ''}
              >
                <i className="fas fa-chart-bar w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">Reports</span>}
              </button>
            )}

            {subadminPermissions.includes('manage_payments') && (
              <button
                onClick={() => handleNavigation('/subadmin/payments')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/payments') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'Payments' : ''}
              >
                <i className="fas fa-credit-card w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">Payments</span>}
              </button>
            )}

            {subadminPermissions.includes('manage_everification_otpcheck') && (
              <button
                onClick={() => handleNavigation('/subadmin/everification-otpcheck')}
                className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname.includes('/subadmin/everification-otpcheck') ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                title={isCollapsed ? 'OTP Check' : ''}
              >
                <i className="fas fa-check-double w-5 shrink-0"></i>
                {!isCollapsed && <span className="text-sm font-medium truncate">OTP Check</span>}
              </button>
            )}

            <div className="mt-auto pb-4 px-3">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="w-full flex items-center justify-center py-3 bg-purple-900/30 hover:bg-purple-900/50 rounded-xl transition-all duration-300 group"
              >
                {isCollapsed ? (
                  <IoIosArrowForward className="w-5 h-5 text-purple-200 group-hover:scale-125 transition-transform" />
                ) : (
                  <div className="flex items-center gap-3">
                    <IoIosArrowBack className="w-5 h-5 text-purple-200 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold uppercase tracking-widest text-purple-100">Collapse</span>
                  </div>
                )}
              </button>
            </div>
          </aside>
        ) : (
          // Agent left-nav: show full nav only if agent is verified
          <aside className={`fixed top-0 left-0 h-screen ${isCollapsed ? 'w-0 md:w-20' : 'w-64'} transition-all duration-300 bg-[#7A306D] text-white flex flex-col pt-4 space-y-1 z-50 border-r border-purple-800 shadow-xl overflow-y-auto overflow-x-hidden`}>
            {!isAgentVerified ? (
              <div className="text-center text-purple-200 p-6">
                <i className="fas fa-user-clock text-4xl mb-4 opacity-50"></i>
                <p className="font-bold text-lg">Account Pending</p>
                <p className="text-xs opacity-70">Your agent account is not verified yet.</p>
              </div>
            ) : (
              <>
                <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-6'} py-4 mb-4`}>
                  <img src={logo} alt="Logo" className="w-10 h-10 bg-white rounded-lg p-1 shrink-0" />
                  {!isCollapsed && <span className="font-bold text-sm tracking-tight text-white truncate">AGENT PANEL</span>}
                </div>

                {agentPermissions.includes('manage_customer') && (
                  <button
                    onClick={() => handleNavigation('/itr-application')}
                    className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/itr-application' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                    title={isCollapsed ? "Customer's" : ''}
                  >
                    <i className="fas fa-file-invoice w-5 shrink-0"></i>
                    {!isCollapsed && <span className="font-medium truncate">Customer's</span>}
                  </button>
                )}

                {agentPermissions.includes('manage_add_new_itr') && (
                  <button
                    onClick={() => handleNavigation('/customer-form')}
                    className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/customer-form' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                    title={isCollapsed ? 'Add New Customer' : ''}
                  >
                    <i className="fas fa-user-plus w-5 shrink-0"></i>
                    {!isCollapsed && <span className="font-medium truncate">Add New Customer</span>}
                  </button>
                )}

                {agentPermissions.includes('manage_itr_history') && (
                  <button
                    onClick={() => handleNavigation('/itr-history')}
                    className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/itr-history' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                    title={isCollapsed ? 'ITR History' : ''}
                  >
                    <i className="fas fa-history w-5 shrink-0"></i>
                    {!isCollapsed && <span className="font-medium truncate">ITR History</span>}
                  </button>
                )}

                {agentPermissions.includes('manage_wallet') && (
                  <button
                    onClick={() => handleNavigation('/wallet')}
                    className={`text-left ${isCollapsed ? 'px-0 justify-center' : 'px-4 mx-3'} py-2 rounded-md transition-all duration-200 flex items-center gap-3 ${location.pathname === '/wallet' ? 'bg-purple-900 shadow-md' : 'hover:bg-purple-600 hover:pl-5'}`}
                    title={isCollapsed ? 'Wallet Transactions' : ''}
                  >
                    <i className="fas fa-wallet w-5 shrink-0"></i>
                    {!isCollapsed && <span className="font-medium truncate">Transaction History</span>}
                  </button>
                )}

                <div className="mt-auto pb-4 px-3">
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="w-full flex items-center justify-center py-3 bg-purple-900/30 hover:bg-purple-900/50 rounded-xl transition-all duration-300 group"
                  >
                    {isCollapsed ? (
                      <IoIosArrowForward className="w-5 h-5 text-purple-200 group-hover:scale-125 transition-transform" />
                    ) : (
                      <div className="flex items-center gap-3">
                        <IoIosArrowBack className="w-5 h-5 text-purple-200 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-xs font-bold uppercase tracking-widest text-purple-100">Collapse</span>
                      </div>
                    )}
                  </button>
                </div>
              </>
            )}
          </aside>
        )
      }
      {/* Premium Toast Notification */}
      {showToast && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[9999] animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white/90 backdrop-blur-md border border-green-100 px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <i className="fas fa-check-circle"></i>
            </div>
            <p className="text-sm font-bold text-gray-800">{toastMessage}</p>
          </div>
        </div>
      )}

      {/* New Message Toast Notification */}
      {notification && (
        <div
          onMouseEnter={handleToastMouseEnter}
          onClick={handleNotificationClick}
          className="fixed top-20 right-6 z-[9999] max-w-sm w-full bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-purple-100 p-4 flex gap-3 cursor-pointer transform hover:scale-[1.02] active:scale-98 transition-all duration-300 animate-toast-in group/toast"
        >
          {/* Avatar or Letter */}
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shrink-0 shadow-lg shadow-purple-100">
            {notification.sender_name?.substring(0, 2).toUpperCase() || 'ME'}
          </div>

          {/* Content info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-gray-800 truncate">
                {notification.sender_name}
              </p>
              <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-purple-100 text-[#7A306D]">
                {notification.sender_role}
              </span>
            </div>
            <p className="text-[9px] font-bold text-purple-500/80 uppercase tracking-widest mt-0.5">
              #{notification.room_name}
            </p>
            <p className="text-xs text-gray-600 mt-1 font-medium line-clamp-2 leading-relaxed">
              {notification.message}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
              }
              setNotification(null);
            }}
            className="text-gray-300 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-50 self-start transition-colors shrink-0"
          >
            <IoMdClose className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-[#7A306D] p-4 text-white flex justify-between items-center">
              <h3 className="font-bold">Crop Profile Photo</h3>
              <button
                onClick={() => {
                  setShowCropper(false);
                  setTempImage(null);
                }}
                className="hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <IoMdClose size={24} />
              </button>
            </div>

            <div className="relative h-80 bg-gray-900">
              <Cropper
                image={tempImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-bold text-gray-400 uppercase tracking-widest">
                  <span>Zoom Level</span>
                  <span>{Math.round(zoom * 100)}%</span>
                </div>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCropper(false);
                    setTempImage(null);
                  }}
                  className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropSave}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-bold rounded-xl shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <i className="fas fa-check"></i>
                  Apply Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
